/**
 * Estado do módulo de embarcações (Zustand). Registra o codec de URL `n` no
 * import — `ui/modules.tsx` importa este arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import { FAMILIAS, type Familia } from './kinds';
import type { Dados, Estilo, Situacao } from './types';

export const DEFAULT_ESTILO: Estilo = {
  tamanho: 7,
  opacidade: 0.95,
  // Uma embarcação que saiu do alcance some do mapa por padrão: manter no ar
  // a posição de horas atrás, sem a pessoa pedir, é o tipo de coisa que faz
  // alguém achar que o rio está mais cheio do que está.
  mostrarSumidas: false,
  // A área de escuta vem LIGADA: sem ela, trecho de rio sem ponto se lê como
  // "sem navegação", quando quase sempre é "sem recepção".
  mostrarEscuta: true,
  familias: [],
};

export const LIMITES = {
  tamanho: { min: 4, max: 14, step: 0.5 },
  opacidade: { min: 0.2, max: 1, step: 0.05 },
} as const;

/** A camada começa desligada: é dado extra, não o assunto principal do app. */
export const DEFAULT_VISIVEL = false;

const situacaoInicial: Situacao = { estado: 'idle', mensagem: null, erro: null };

const DADOS_VAZIOS: Dados = {
  navios: [],
  anonimos: [],
  escuta: [],
  atribuicao: {},
  gerado: null,
  trilhaHoras: 24,
};

export interface ShipState {
  visivel: boolean;
  estilo: Estilo;
  situacao: Situacao;
  dados: Dados;
  /** MMSI selecionado: é dele que a trilha aparece no mapa. */
  selecionado: number | null;

  setVisivel: (on: boolean) => void;
  setEstilo: (patch: Partial<Estilo>) => void;
  alternarFamilia: (f: Familia) => void;
  setSituacao: (s: Situacao) => void;
  setDados: (d: Dados) => void;
  selecionar: (mmsi: number | null) => void;
}

export const useShipStore = create<ShipState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  estilo: DEFAULT_ESTILO,
  situacao: situacaoInicial,
  dados: DADOS_VAZIOS,
  selecionado: null,

  setVisivel: (on) => set({ visivel: on }),
  setEstilo: (patch) => set((s) => ({ estilo: { ...s.estilo, ...patch } })),
  alternarFamilia: (f) =>
    set((s) => {
      const tem = s.estilo.familias.includes(f);
      const familias = tem ? s.estilo.familias.filter((x) => x !== f) : [...s.estilo.familias, f];
      // Ligar todas é o mesmo que não filtrar: guarda a lista vazia para o
      // link ficar curto e para o padrão continuar sendo "mostrar tudo".
      return { estilo: { ...s.estilo, familias: familias.length === FAMILIAS.length ? [] : familias } };
    }),
  setSituacao: (situacao) => set({ situacao }),
  setDados: (dados) => set({ dados }),
  selecionar: (selecionado) => set({ selecionado }),
}));

/** `true` quando a família deve ser desenhada (lista vazia = todas). */
export function familiaVisivel(estilo: Estilo, f: Familia): boolean {
  return estilo.familias.length === 0 || estilo.familias.includes(f);
}

// ---------------------------------------------------------------------------
// Link compartilhável — chave `n`
// ---------------------------------------------------------------------------

/**
 * `n=1` liga a camada; as letras seguintes guardam só o que difere do padrão
 * (`t` tamanho, `o` opacidade, `s` inverte "mostrar sumidas", `e` inverte a
 * área de escuta, `f` lista as famílias filtradas pela inicial).
 * Ex.: `n=1t9fct`. Desligada (o padrão) omite o parâmetro.
 *
 * Como nas estações, as chaves booleanas significam "o contrário do padrão",
 * nunca um valor fixo: assim o link continua curto e correto mesmo se o
 * padrão mudar.
 */
const INICIAL: Readonly<Record<Familia, string>> = {
  carga: 'c',
  tanque: 't',
  passageiros: 'p',
  pesca: 'q',
  reboque: 'r',
  servico: 's',
  recreio: 'e',
  outro: 'o',
};
const POR_INICIAL = new Map(Object.entries(INICIAL).map(([f, i]) => [i, f as Familia]));

export function encodeShips(s: Pick<ShipState, 'visivel' | 'estilo'>): string | null {
  if (!s.visivel) return null;
  let out = '1';
  if (s.estilo.tamanho !== DEFAULT_ESTILO.tamanho) out += `t${s.estilo.tamanho}`;
  if (s.estilo.opacidade !== DEFAULT_ESTILO.opacidade) out += `o${s.estilo.opacidade}`;
  if (s.estilo.mostrarSumidas !== DEFAULT_ESTILO.mostrarSumidas) out += 's';
  if (s.estilo.mostrarEscuta !== DEFAULT_ESTILO.mostrarEscuta) out += 'e';
  if (s.estilo.familias.length > 0) {
    out += `f${s.estilo.familias.map((f) => INICIAL[f as Familia] ?? '').join('')}`;
  }
  return out;
}

export function decodeShips(value: string): Partial<Pick<ShipState, 'visivel' | 'estilo'>> | null {
  if (!value || value[0] !== '1') return null;
  const estilo: Estilo = { ...DEFAULT_ESTILO };

  const tamanho = /t(\d+(?:\.\d+)?)/.exec(value);
  if (tamanho) estilo.tamanho = limitar(Number(tamanho[1]), LIMITES.tamanho.min, LIMITES.tamanho.max, DEFAULT_ESTILO.tamanho);

  const opac = /o(\d*\.?\d+)/.exec(value);
  if (opac) estilo.opacidade = limitar(Number(opac[1]), LIMITES.opacidade.min, LIMITES.opacidade.max, DEFAULT_ESTILO.opacidade);

  // `s` e `e` só valem fora do bloco de famílias, que é o resto da string.
  const semFamilias = value.replace(/f[a-z]*$/, '');
  if (semFamilias.includes('s')) estilo.mostrarSumidas = !DEFAULT_ESTILO.mostrarSumidas;
  if (semFamilias.includes('e')) estilo.mostrarEscuta = !DEFAULT_ESTILO.mostrarEscuta;

  const fam = /f([a-z]*)$/.exec(value);
  if (fam?.[1]) {
    const lista = [...fam[1]].map((i) => POR_INICIAL.get(i)).filter((f): f is Familia => f !== undefined);
    estilo.familias = lista.length === FAMILIAS.length ? [] : lista;
  }

  return { visivel: true, estilo };
}

function limitar(n: number, min: number, max: number, padrao: number): number {
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : padrao;
}

registerUrlCodec({
  key: 'n',
  encode: () => encodeShips(useShipStore.getState()),
  decode: (value) => {
    const s = decodeShips(value);
    if (s) useShipStore.setState(s);
  },
  subscribe: (onChange) =>
    useShipStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.estilo !== b.estilo) onChange();
    }),
});
